/**
 * ChatComposer — text input + image/file/voice attachment controls for
 * ConnectionChat. Owns its own MediaRecorder state (recording lifecycle).
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Send, Paperclip, Mic, Image as ImageIcon, Loader2, Square,
} from "lucide-react";

const MAX_TEXT = 4000;

const KIND_FROM_MIME = (mime = "") => {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  return "file";
};

export default function ChatComposer({
  canSend,
  isExpired,
  sending,
  uploading,
  onSendText,
  onUploadFile,
}) {
  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const submit = async () => {
    const body = text.trim();
    if (!body || !canSend) return;
    setText("");
    const ok = await onSendText(body);
    if (!ok) setText(body);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onPickImage = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onUploadFile(f, "image");
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) onUploadFile(f, KIND_FROM_MIME(f.type || ""));
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone is not available in this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      audioChunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) audioChunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const f = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await onUploadFile(f, "audio");
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
  };

  if (recording) {
    return (
      <div className="flex items-center gap-2 rounded-full bg-red-50 dark:bg-red-500/15 border border-red-200/60 dark:border-red-500/30 px-4 py-2.5">
        <span className="inline-flex items-center gap-2 text-red-700 dark:text-red-300 text-sm font-medium">
          <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          Recording…
        </span>
        <Button onClick={stopRecording} size="sm" className="ml-auto rounded-full" data-testid="chat-stop-record">
          <Square className="h-3.5 w-3.5 mr-1" /> Stop & send
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-end gap-2 rounded-2xl border border-border bg-card p-2">
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="chat-image-input" />
        <input ref={fileInputRef} type="file" className="hidden" onChange={onPickFile} data-testid="chat-file-input" />

        <button
          onClick={() => imageInputRef.current?.click()}
          disabled={!canSend}
          className="h-9 w-9 grid place-items-center rounded-xl text-muted-foreground hover:bg-muted disabled:opacity-40 transition shrink-0"
          aria-label="Attach image" data-testid="chat-attach-image" title="Attach image"
        >
          <ImageIcon className="h-4 w-4" />
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!canSend}
          className="h-9 w-9 grid place-items-center rounded-xl text-muted-foreground hover:bg-muted disabled:opacity-40 transition shrink-0"
          aria-label="Attach file" data-testid="chat-attach-file" title="Attach file"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <button
          onClick={startRecording}
          disabled={!canSend}
          className="h-9 w-9 grid place-items-center rounded-xl text-muted-foreground hover:bg-muted disabled:opacity-40 transition shrink-0"
          aria-label="Record voice" data-testid="chat-record" title="Record voice note"
        >
          <Mic className="h-4 w-4" />
        </button>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
          onKeyDown={onKey}
          disabled={!canSend}
          placeholder={isExpired ? "Conversation expired — read-only" : "Type a message…"}
          rows={1}
          className="flex-1 min-h-[36px] max-h-32 resize-none rounded-xl bg-transparent px-2 py-2 text-sm focus:outline-none disabled:opacity-50"
          data-testid="chat-input"
        />

        <Button
          onClick={submit}
          disabled={!canSend || !text.trim()}
          size="sm"
          className="rounded-xl h-9 px-4 shrink-0"
          data-testid="chat-send"
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {uploading && (
        <p className="mt-1.5 text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid="chat-upload-status">
          <Loader2 className="h-3 w-3 animate-spin" /> Uploading attachment…
        </p>
      )}
    </>
  );
}
